import { request } from '@/shared/utils/requestBuilder';

import type {
  AmbienteDoDestino,
  DesfechoDoReenvio,
  DestinoDoWebhook,
  DetalheDoEvento,
  EdicaoDoDestino,
  ErroDaCentral,
  EscolhaDeEventos,
  EventoDoWebhook,
  FiltroDosEventos,
  IntegracaoDeWebhook,
  LeituraDaCentral,
  Pagina,
  PedidoDeReenvio,
  AlteracaoDaIntegracao,
  ResultadoDaRetomada,
  ResultadoDoTeste,
} from '../types/centralWebhooksTypes';

/**
 * Único ponto da tela que conhece rota e envelope da Central (contrato
 * `central-webhooks-rotas.md`, versão 1). O `BASE_URL` do `requestBuilder` já termina em `/api`.
 */
const rotaDaCentral = (escritorioId: string) =>
  `/escritorios/${encodeURIComponent(escritorioId)}/webhooks`;

const SEGMENTO_DO_AMBIENTE: Record<AmbienteDoDestino, string> = {
  HOMOLOGACAO: 'homologacao',
  PRODUCAO: 'producao',
};

const rotaDoDestino = (escritorioId: string, ambiente: AmbienteDoDestino) =>
  `${rotaDaCentral(escritorioId)}/destinos/${SEGMENTO_DO_AMBIENTE[ambiente]}`;

type Metodo = 'GET' | 'POST' | 'PATCH';

/**
 * Mesma razão das rotas de chaves: o 403 daqui é recusa de permissão de quem administra a
 * integração (`WEBHOOK_ACESSO_NEGADO`), e a recuperação de workspace do `requestBuilder` trocaria
 * esse 403 por outro escritório.
 */
const chamar = (endpoint: string, method: Metodo, body?: object) =>
  request({ endpoint, method, body, recuperarWorkspaceNo403: false });

const MENSAGEM_POR_CODIGO: Record<string, string> = {
  WEBHOOK_ACESSO_NEGADO: 'Você não tem permissão para administrar as integrações deste escritório.',
  WEBHOOK_INTEGRACAO_INEXISTENTE: 'A integração não existe mais. A tela foi atualizada.',
  WEBHOOK_INTEGRACAO_JA_EXISTE: 'Este escritório já tem uma integração. A tela foi atualizada.',
  WEBHOOK_URL_BLOQUEADA: 'Este endereço aponta para uma rede interna e não é permitido.',
  WEBHOOK_DESTINO_INCOMPLETO: 'Cadastre a URL e gere o segredo deste endereço antes.',
  WEBHOOK_DESTINO_PAUSADO: 'O endereço está pausado. Use "Retomar".',
  WEBHOOK_DESTINO_EM_RASCUNHO: 'O endereço ainda não foi ativado.',
  WEBHOOK_DESTINO_NAO_PAUSADO: 'O endereço não está pausado. A tela foi atualizada.',
  WEBHOOK_DESTINO_INEXISTENTE: 'O endereço não existe mais. A tela foi atualizada.',
  WEBHOOK_EVENTO_INEXISTENTE: 'O evento não foi encontrado.',
  WEBHOOK_CURSOR_INVALIDO: 'Não foi possível continuar a lista. Recarregue os eventos.',
  WEBHOOK_CHAVE_INDISPONIVEL:
    'O serviço de webhooks está indisponível no momento. Tente mais tarde ou fale com o suporte.',
};

const MENSAGEM_POR_STATUS: Record<number, string> = {
  400: 'O pedido não foi aceito. Confira os dados informados.',
  403: MENSAGEM_POR_CODIGO.WEBHOOK_ACESSO_NEGADO,
  404: 'O item não foi encontrado. A tela foi atualizada.',
  409: 'A integração mudou enquanto você editava. A tela foi atualizada.',
};
const MENSAGEM_GENERICA = 'Não foi possível concluir a operação. Tente novamente.';

/**
 * Rejeição tipada que o thunk passa adiante como `ErroDaCentral`. No 400 sem código conhecido vale
 * a mensagem do core: é ela que diz o que o formulário errou (cabeçalho reservado, usuário com ":").
 */
export class FalhaDaCentral extends Error {
  readonly erro: ErroDaCentral;

  constructor(erro: ErroDaCentral) {
    super(erro.mensagem);
    this.name = 'FalhaDaCentral';
    this.erro = erro;
  }
}

interface CorpoDaApi<T> {
  success?: boolean;
  data?: T;
  error?: { code?: unknown; message?: unknown };
}

function mensagemDaFalha(status: number, codigo: string | null, doCore: string | null): string {
  if (codigo && MENSAGEM_POR_CODIGO[codigo]) return MENSAGEM_POR_CODIGO[codigo];
  if (status === 400 && doCore) return doCore;
  return MENSAGEM_POR_STATUS[status] ?? MENSAGEM_GENERICA;
}

async function lerResposta<T>(response: Response): Promise<T> {
  const json = (await response.json().catch(() => null)) as CorpoDaApi<T> | null;
  if (!response.ok) {
    const codigo = typeof json?.error?.code === 'string' ? json.error.code : null;
    const doCore = typeof json?.error?.message === 'string' ? json.error.message : null;
    throw new FalhaDaCentral({
      status: response.status,
      codigo,
      mensagem: mensagemDaFalha(response.status, codigo, doCore),
    });
  }
  if (json == null || json.data === undefined) {
    throw new FalhaDaCentral({
      status: response.status,
      codigo: null,
      mensagem: MENSAGEM_GENERICA,
    });
  }
  return json.data;
}

interface SegredoGeradoDaApi {
  destino: DestinoDoWebhook;
  segredo: string;
  rotacao: boolean;
}

interface RetomadaDaApi extends ResultadoDaRetomada {
  destino: DestinoDoWebhook;
}

function parametrosDosEventos(filtro: FiltroDosEventos, cursor: string | null, limite: number) {
  const parametros = new URLSearchParams({
    filtro: filtro.estado === 'FALHAS' ? 'falhas' : 'tudo',
    limite: String(limite),
  });
  if (filtro.ambiente) parametros.set('ambiente', SEGMENTO_DO_AMBIENTE[filtro.ambiente]);
  if (cursor) parametros.set('cursor', cursor);
  return parametros.toString();
}

export const centralWebhooksService = {
  async ler(escritorioId: string): Promise<LeituraDaCentral> {
    const response = await chamar(rotaDaCentral(escritorioId), 'GET');
    return lerResposta<LeituraDaCentral>(response);
  },

  async criarIntegracao(
    escritorioId: string,
    escolha: EscolhaDeEventos,
  ): Promise<IntegracaoDeWebhook> {
    const response = await chamar(rotaDaCentral(escritorioId), 'POST', escolha);
    return lerResposta<IntegracaoDeWebhook>(response);
  },

  async salvarEventos(
    escritorioId: string,
    escolha: EscolhaDeEventos,
  ): Promise<IntegracaoDeWebhook> {
    const response = await chamar(rotaDaCentral(escritorioId), 'PATCH', escolha);
    return lerResposta<IntegracaoDeWebhook>(response);
  },

  async editarDestino(
    escritorioId: string,
    ambiente: AmbienteDoDestino,
    edicao: EdicaoDoDestino,
  ): Promise<DestinoDoWebhook> {
    const response = await chamar(rotaDoDestino(escritorioId, ambiente), 'PATCH', edicao);
    return lerResposta<DestinoDoWebhook>(response);
  },

  /** Separa o segredo do DTO já aqui: o resto da tela nunca vê os dois no mesmo objeto. */
  async gerarSegredo(
    escritorioId: string,
    ambiente: AmbienteDoDestino,
  ): Promise<{ destino: DestinoDoWebhook; segredo: string }> {
    const response = await chamar(`${rotaDoDestino(escritorioId, ambiente)}/segredo`, 'POST');
    const { destino, segredo } = await lerResposta<SegredoGeradoDaApi>(response);
    if (typeof segredo !== 'string' || segredo.length === 0) {
      throw new FalhaDaCentral({
        status: response.status,
        codigo: null,
        mensagem: MENSAGEM_GENERICA,
      });
    }
    return { destino, segredo };
  },

  async enviarTeste(escritorioId: string, ambiente: AmbienteDoDestino): Promise<ResultadoDoTeste> {
    const response = await chamar(`${rotaDoDestino(escritorioId, ambiente)}/teste`, 'POST');
    return lerResposta<ResultadoDoTeste>(response);
  },

  async ativar(escritorioId: string, ambiente: AmbienteDoDestino): Promise<DestinoDoWebhook> {
    const response = await chamar(`${rotaDoDestino(escritorioId, ambiente)}/ativar`, 'POST');
    return lerResposta<DestinoDoWebhook>(response);
  },

  async pausar(escritorioId: string, ambiente: AmbienteDoDestino): Promise<DestinoDoWebhook> {
    const response = await chamar(`${rotaDoDestino(escritorioId, ambiente)}/pausar`, 'POST');
    return lerResposta<DestinoDoWebhook>(response);
  },

  async retomar(escritorioId: string, ambiente: AmbienteDoDestino): Promise<RetomadaDaApi> {
    const response = await chamar(`${rotaDoDestino(escritorioId, ambiente)}/retomar`, 'POST');
    return lerResposta<RetomadaDaApi>(response);
  },

  async listarEventos(
    escritorioId: string,
    filtro: FiltroDosEventos,
    cursor: string | null,
    limite: number,
  ): Promise<Pagina<EventoDoWebhook>> {
    const response = await chamar(
      `${rotaDaCentral(escritorioId)}/eventos?${parametrosDosEventos(filtro, cursor, limite)}`,
      'GET',
    );
    return lerResposta<Pagina<EventoDoWebhook>>(response);
  },

  async lerEvento(escritorioId: string, eventoId: string): Promise<DetalheDoEvento> {
    const response = await chamar(
      `${rotaDaCentral(escritorioId)}/eventos/${encodeURIComponent(eventoId)}`,
      'GET',
    );
    return lerResposta<DetalheDoEvento>(response);
  },

  /** 202: o core aceitou o pedido; cada tentativa sai depois, pela fila do entregador. */
  async reenviar(escritorioId: string, pedido: PedidoDeReenvio): Promise<DesfechoDoReenvio> {
    const response = await chamar(
      `${rotaDaCentral(escritorioId)}/eventos/reenviar`,
      'POST',
      pedido,
    );
    return lerResposta<DesfechoDoReenvio>(response);
  },

  async listarHistorico(escritorioId: string, limite: number): Promise<AlteracaoDaIntegracao[]> {
    const response = await chamar(
      `${rotaDaCentral(escritorioId)}/historico?limite=${limite}`,
      'GET',
    );
    return lerResposta<AlteracaoDaIntegracao[]>(response);
  },
};
